import { Link } from 'react-router-dom';
import { money } from '../../../lib/api';
import type { Chore } from '../../../shared/types';
import type { Resource } from '../useResource';
import { choresFor } from '../homeData';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../ui';

/**
 * A kid's chores: the daily ones are today's list, the rest are this week's.
 *
 * The hub never marks a chore done — it can't see which ones were already
 * finished today (see `needs`), and a double-submitted chore is a broken promise
 * about money. Every row goes to the Chores screen, which owns that state.
 */
export default function KidChoresCard({
  res,
  memberId,
  color,
}: {
  res: Resource<Chore[]>;
  memberId: number | null;
  color: string | null;
}) {
  const { data, loading, error, reload } = res;
  const mine = choresFor(data, memberId);
  const today = mine.filter((c) => c.cadence === 'daily');
  const thisWeek = mine.filter((c) => c.cadence !== 'daily');

  return (
    <Card className="p-5 sm:p-6">
      <CardTitle icon="broom" action={{ to: '/chores', label: 'All chores' }}>
        Your chores
      </CardTitle>

      <div className="mt-4">
        {loading && <LoadingRows rows={3} />}
        {!loading && error && <ErrorState what="your chores" onRetry={reload} />}

        {!loading && !error && mine.length === 0 && (
          <Empty
            message="Nothing on your list right now. When a grown-up adds a chore, it shows up here."
            action={{ to: '/chores', label: 'Open chores' }}
          />
        )}

        {!loading && !error && mine.length > 0 && (
          <div className="space-y-5">
            <ChoreGroup
              heading={today.length > 0 ? 'Today' : null}
              chores={today}
              color={color}
            />
            <ChoreGroup
              heading={thisWeek.length > 0 ? 'This week' : null}
              chores={thisWeek}
              color={color}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

function ChoreGroup({
  heading,
  chores,
  color,
}: {
  heading: string | null;
  chores: Chore[];
  color: string | null;
}) {
  if (chores.length === 0) return null;
  return (
    <div>
      {heading && (
        <h3 className="text-xs font-semibold tracking-wide text-ink-faint uppercase">{heading}</h3>
      )}
      <ul className="mt-2 space-y-2">
        {chores.map((c) => (
          <li key={c.id}>
            <Link
              to="/chores"
              className="flex min-h-11 items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2 transition-colors hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white"
                style={{ backgroundColor: color ?? 'var(--color-sage)' }}
              >
                <Icon name="check" className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{c.name}</span>
                {c.description && (
                  <span className="block truncate text-xs text-ink-soft">{c.description}</span>
                )}
              </span>
              {c.rate > 0 && (
                <span className="shrink-0 rounded-full bg-sage-soft px-2 py-0.5 text-xs font-semibold text-sage tabular-nums">
                  {money(c.rate)}
                </span>
              )}
              <Icon name="arrow" className="h-4 w-4 shrink-0 text-ink-faint" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
